// Card de sugestão reutilizável (lista completa e versão compacta).
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Check, ChevronDown, ChevronUp, PiggyBank, TrendingUp, Wrench } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { RecommendationDto } from '@impulso/shared/api/recommendations'
import { errorMessage } from '../../lib/api'
import { Platform } from '../../ui'
import { useToast } from '../../ui/toast'
import { revertMany } from '../actions/api'
import { useApplyRecommendation, useDismissRecommendation, recKeys } from './api'

const KINDS = {
  growth: { icon: TrendingUp, cls: 'green', label: 'Mais clientes' },
  savings: { icon: PiggyBank, cls: 'blue', label: 'Economia' },
  fix: { icon: Wrench, cls: 'amber', label: 'Correção' },
} as const

const CONFIDENCE: Record<string, string> = { high: 'alta', medium: 'média', low: 'baixa' }

export default function SuggestionCard({ s, companyId, compact, canApply }: { s: RecommendationDto; companyId: string; compact?: boolean; canApply: boolean }) {
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const [applied, setApplied] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const apply = useApplyRecommendation(companyId)
  const dismiss = useDismissRecommendation(companyId)
  const k = KINDS[s.kind]
  const busy = apply.isPending || dismiss.isPending

  const onPrimary = () => {
    apply.mutate(s.id, {
      onSuccess: (res) => {
        if (res.ctaLink) { navigate(res.ctaLink); return }
        setApplied(true)
        const ids = res.actionIds
        toast(ids.length === 1 ? 'Mudança enviada. Acompanhe no histórico.' : `${ids.length} mudanças enviadas.`, async () => {
          const r = await revertMany(companyId, ids)
          void qc.invalidateQueries({ queryKey: recKeys.all(companyId) })
          void qc.invalidateQueries({ queryKey: ['actions', companyId] })
          if (r.failed.length) toast(r.failed[0]!.includes('concluídas') ? 'A mudança ainda está sendo feita. Desfaça pelo Histórico em instantes.' : r.failed[0]!)
          else toast('Mudança desfeita.')
        })
      },
      onError: (err) => toast(errorMessage(err)),
    })
  }

  const onDismiss = () => {
    dismiss.mutate({ id: s.id, reason: reason.trim() || null }, {
      onSuccess: () => toast('Tudo bem. Não vamos sugerir isso de novo por um tempo.'),
      onError: (err) => toast(errorMessage(err)),
    })
  }

  if (applied && !compact) {
    return (
      <div className="card row" style={{ background: 'var(--green-bg)', borderColor: 'transparent' }}>
        <span className="ic green"><Check size={18} /></span>
        <div>
          <div className="strong">{s.title}</div>
          <div className="small muted">Aplicada agora. Vamos acompanhar o resultado e te avisar em 7 dias.</div>
        </div>
      </div>
    )
  }
  if (applied) return null

  return (
    <div className={compact ? 'list-item' : 'card'} style={compact ? undefined : { display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <span className={`ic ${k.cls}`}><k.icon size={18} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {!compact && (
          <div className="row wrap" style={{ gap: 8 }}>
            <span className={`badge ${k.cls}`}>{k.label}</span>
            {s.priority === 'high' && <span className="badge red">Prioridade</span>}
            {s.provider && <Platform platform={s.provider} />}
          </div>
        )}
        <h3 style={{ marginTop: compact ? 0 : 8, overflowWrap: 'anywhere' }}>{s.title}</h3>
        {!compact && <p className="muted mt-8">{s.why}</p>}
        <div className="row wrap mt-8" style={{ gap: 14 }}>
          <span className="strong" style={{ color: 'var(--green)' }}>{s.impactText}</span>
          {!compact && <span className="small muted">Confiança: {CONFIDENCE[s.confidence]}</span>}
        </div>

        {open && s.changes.length > 0 && (
          <div className="card mt-12" style={{ background: 'var(--surface-2)', boxShadow: 'none', padding: 14 }}>
            <div className="small strong muted" style={{ marginBottom: 8 }}>O QUE VAI MUDAR</div>
            <div className="stack" style={{ gap: 8 }}>
              {s.changes.map((c) => (
                <div key={c.label}>
                  <div className="small muted">{c.label}</div>
                  <div className="compare" style={{ gridTemplateColumns: 'minmax(0, max-content) auto minmax(0, 1fr)' }}>
                    <span className="from">{c.from}</span>
                    <ArrowRight size={14} className="muted" />
                    <span className="to">{c.to}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="small muted mt-12">{s.ctaLink ? 'Nada é publicado sem você revisar.' : 'Você pode desfazer a qualquer momento. Tudo fica registrado no histórico.'}</p>
          </div>
        )}

        {asking ? (
          <div className="stack mt-12" style={{ gap: 8 }}>
            <label className="small muted" htmlFor={`why-${s.id}`}>Quer contar o motivo? (opcional, ajuda a sugerir melhor)</label>
            <input id={`why-${s.id}`} className="input" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: essa campanha é só para a data comemorativa" />
            <div className="row wrap" style={{ gap: 8 }}>
              <button className="btn sm" disabled={busy} onClick={onDismiss}>Confirmar "agora não"</button>
              <button className="btn ghost sm" onClick={() => setAsking(false)}>Voltar</button>
            </div>
          </div>
        ) : (
          <div className="row wrap mt-12" style={{ gap: 8 }}>
            {canApply && (
              <button className="btn primary sm" disabled={busy} onClick={onPrimary}>
                {apply.isPending ? 'Aplicando…' : s.ctaLabel ?? 'Aplicar'}
              </button>
            )}
            {!compact && s.changes.length > 0 && (
              <button className="btn sm" onClick={() => setOpen(!open)} aria-expanded={open}>
                {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />} {open ? 'Esconder detalhes' : 'Ver o que muda'}
              </button>
            )}
            {canApply && <button className="btn ghost sm" disabled={busy} onClick={() => setAsking(true)}>Agora não</button>}
            {!canApply && <span className="small muted">Peça para quem gerencia a conta aprovar.</span>}
          </div>
        )}
      </div>
    </div>
  )
}
